'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Keep the canvas's last visible normal-flow size while hidden or fullscreen. */
export default function useCanvasSize(canvasRef, aspect, fullscreen) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [snap, setSnap] = useState(false);
  const measuredWidth = useRef(0);

  // A real width change snaps; an aspect change is a layout switch that glides.
  useEffect(() => {
    if (!snap) return undefined;
    const id = requestAnimationFrame(() => setSnap(false));
    return () => cancelAnimationFrame(id);
  }, [snap]);

  useLayoutEffect(() => {
    const el = canvasRef.current;
    if (!el || fullscreen) return undefined;
    const update = () => {
      const width = el.clientWidth;
      // display:none is not a resize of the drawing: keeping its last size
      // also keeps the replay whose frames were prepared for that size.
      if (width <= 0) return;
      if (measuredWidth.current && measuredWidth.current !== width) setSnap(true);
      measuredWidth.current = width;
      const height = width / aspect;
      setSize((previous) => (previous.width === width && previous.height === height ? previous : { width, height }));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [canvasRef, aspect, fullscreen]);

  return [size, snap];
}
